import { SiteUrls } from '@tableau/shared-urls';
import * as _ from 'underscore';

import { HiddenUserStyles } from '../../app/spaces/SpacesStyles';
import { UserMapper } from '../mappers/UserMapper';
import { Module } from '../Module';
import { DataSources } from '../../content/resources/Datasources';
import { IDataSource } from '../ServerApi/types/IDataSource';
import { IResult } from '../../httpClient/serverApi/types/IResponse';
import { getSessionManagementServiceInstance } from '../../sessionManagement/SessionManagementService';
import { ToasterService, ToastType } from '../services/ToasterService';

export interface IDataSourceCertificationEditorModel {
  isCertified: boolean;
  note: string;
  certifier: {
    displayName: string;
    url: string;
    customUserStyle?: React.CSSProperties;
  };
}

export interface IDataSourceCertificationEditorState {
  canEdit: boolean;
  isBusy: boolean;
  isEditMode: boolean;
  isDirty: () => boolean;
}

export interface IDataSourceCertificationEditorControls {
  enterEditMode: () => void;
  exitEditMode: () => void;
  save: ($event: JQueryEventObject) => void;
}

export interface IDataSourceCertificationEditorScope extends ng.IScope {
  canSetCertification: boolean;
  certificationStatusUpdated: ($event: JQueryEventObject) => void;
  controls: IDataSourceCertificationEditorControls;
  datasource: IDataSource;
  model: IDataSourceCertificationEditorModel;
  master: IDataSourceCertificationEditorModel;
  state: IDataSourceCertificationEditorState;
}

Module.instance().directive('tbDataSourceCertificationEditor', [
  '$translate',
  ($translate: ng.translate.ITranslateService) => {
    return {
      restrict: 'A',
      replace: true,
      templateUrl: 'dataSourceCertificationEditor.html',
      scope: {
        datasource: '=',
        canSetCertification: '=',
        certificationStatusUpdated: '='
      },
      link: (scope: IDataSourceCertificationEditorScope) => {
        const serverService = getSessionManagementServiceInstance();
        const siteUrls = new SiteUrls({siteUrlName: serverService.sessionSiteUrlName(), queryString: window.location.search});
        const certifiedBy = UserMapper.mapOne(scope.datasource.certificationDetails.certifier);

        // initialize the UI model
        const initData = scope.datasource.certificationDetails;
        const certifierExists = initData.certifier && initData.certifier.domainName;
        scope.model = {
          isCertified:   initData.isCertified,
          note:          initData.certificationNote,
          certifier: {
            displayName: certifiedBy.displayName,
            url: certifierExists ? siteUrls.userUrl(initData.certifier.username, initData.certifier.domainName) : '',
            customUserStyle: certifiedBy.isHiddenInternalUser ? HiddenUserStyles.HiddenUserStyle as React.CSSProperties : null,
          }
        };

        // initialize the UI state
        scope.state = {
          canEdit:    scope.canSetCertification,
          isBusy:     false,
          isEditMode: false,
          isDirty:    () => _.isEqual(scope.master, scope.model)
        };

        // controls for user actions
        scope.controls = {
          enterEditMode: () => {
            scope.state.isEditMode = true;
            scope.master = _.clone(scope.model);
          },
          exitEditMode: () => {
            scope.state.isEditMode = false;
            scope.model = scope.master;
          },
          save: ($event: JQueryEventObject) => {
            scope.state.isBusy = true;
            const params = {
              id: scope.datasource.id,
              isCertified: scope.model.isCertified,
              certificationNote: scope.model.note
            };
            DataSources.setCertification(params).then((result: IResult) => {
              if (result.errors) { // unexpected server error
                ToasterService.instance.pop({ type: ToastType.Error, text: $translate.instant('datasourceCertification_failed')});
              } else { // successful setCertification
                ToasterService.instance.pop({ type: ToastType.Info, text: $translate.instant('datasourceCertification_success')});
                if (scope.model.isCertified) {
                  const certifier = serverService.sessionUserInfo();
                  scope.model.certifier = {
                    displayName: certifier.displayName,
                    url: siteUrls.userUrl(certifier.username, certifier.domainName)
                  };
                } else {
                  scope.model = <IDataSourceCertificationEditorModel>{};
                }
                scope.state.isEditMode = false;
                scope.certificationStatusUpdated($event);
              }
              scope.state.isBusy = false;
            });
          }

        };

      }
    };
  }
]);
